<template>
  <div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" />
    <import-page
      v-if="canImport"
      :key="kind"
      :title="title"
      :template-url="templateUrl"
      :columns="columns"
      :loading="loading"
      :import-loading="saving"
      :limit="1000"
      :preview-list="rows"
      :is-valid="valid"
      @on-preview="preview"
      @on-upload-error="uploadError"
      @on-import="submit"
    />
  </div>
</template>

<script>
import ImportPage from "@/components/import-page";
import api from "@/api/warehouseReceipt";

export default {
  name: "WarehouseReceiptOrderImport",
  components: { ImportPage },
  props: { kind: { type: String, required: true } },
  data() {
    return { rows: [], loading: false, saving: false, error: "", canImport: false };
  },
  async created() {
    try {
      const user = await api.currentUser();
      this.canImport = !!user && !user.customerId;
      if (!this.canImport) {
        this.error = "当前账号不允许导入仓单";
      }
    } catch (error) {
      this.error = error.msg || error.message || "用户信息加载失败，请刷新重试";
    }
  },
  watch: {
    kind() {
      this.rows = [];
      if (this.canImport) {
        this.error = "";
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    next(!this.loading && !this.saving);
  },
  beforeRouteUpdate(to, from, next) {
    next(!this.loading && !this.saving);
  },
  computed: {
    title() {
      return this.kind === "inbound" ? "导入仓单入库单" : "导入仓单出库单";
    },
    templateUrl() {
      const name = this.kind === "inbound" ? "仓单入库单导入" : "仓单出库单导入";
      return `${process.env.BASE_URL}templates/${name}.xlsx`;
    },
    valid() {
      return this.rows.length > 0 && this.rows.length <= 1000 && this.rows.every(row => !row.errorMsg);
    },
    columns() {
      const common = [
        { title: "序号", key: "rowNo", width: 70, render: (h, { $index }) => h("span", $index + 1) },
        { title: "客户单号", key: "customerOrderNo", minWidth: 180 },
        { title: "货主ID", key: "customerId", minWidth: 150 }
      ];
      const fields = this.kind === "inbound"
        ? [
            { title: "商品料号", key: "itemNo", minWidth: 160 },
            { title: "入库数量", key: "quantity", width: 110 },
            { title: "单价", key: "unitPrice", width: 110 },
            { title: "海关币制编码", key: "currency", width: 130 }
          ]
        : [
            { title: "WMS效期批次号", key: "batchId", minWidth: 180 },
            { title: "出库数量", key: "quantity", width: 110 }
          ];
      return common.concat(fields, [{ title: "校验信息", key: "errorMsg", minWidth: 240 }]);
    }
  },
  methods: {
    uploadError(message) {
      this.rows = [];
      this.error = message;
    },
    async preview(fileUrl) {
      if (!this.canImport || this.loading || this.saving) return;
      this.rows = [];
      this.error = "";
      if (!fileUrl || !/\.xlsx?(?:\?|$)/i.test(fileUrl)) {
        this.error = "请先上传xls或xlsx文件";
        return;
      }
      this.loading = true;
      try {
        const result = await api[this.kind].importPreview(fileUrl);
        this.rows = result.value || [];
        if (!this.rows.length || this.rows.length > 1000) this.error = "导入明细必须为1至1000行";
      } catch (error) {
        this.error = error.msg || error.message || "预览失败，请重新上传";
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.canImport || this.loading || this.saving || !this.valid) return;
      this.saving = true;
      this.error = "";
      try {
        const result = await api[this.kind].batchImport(this.rows);
        if (!result.value) throw new Error("导入失败");
        this.$message.success("导入成功");
        this.rows = [];
        this.saving = false;
        this.$router.push({
          name: this.kind === "inbound" ? "warehouseReceiptInbound" : "warehouseReceiptOutbound"
        });
      } catch (error) {
        this.error = error.msg || error.message || "导入失败，请重新预览后重试";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
